<script setup lang="ts">
import { equalYearMonthInfo } from '@/appLogic/interfaces/date';
import { changeDayChart } from '@/appLogic/view/orderChart';
import Chart from '@/components/atom/Chart.vue';
import { diContainer } from '@/coreContainer/container';
import { Order } from '@/coreDomainsModule/entities/order';
import { IOrderBasis, IOrderBasisNextMonthlyData } from '@/coreDomainsModule/entities/orderBasis';
import { AnnounceFactory } from '@/coreDomainsModule/factories/announceFactory';
import { ArrivalFactory } from '@/coreDomainsModule/factories/ArrivalFactory';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';
import { DateService } from '@/coreDomainsModule/services/dateService';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { i18n } from '@/i18n';
import { computed, ref, watchEffect } from 'vue';

const t = i18n.t;

const TABLE_NUM_CELL_WIDTH = 105;
const CHART_PERIOD = 180;

const NextMonthlyTableGridTemplateColumn = ['140px', ...new Array(6).fill(`${TABLE_NUM_CELL_WIDTH}px`)];
const PastMonthlyTableGridTemplateColumn = ['66px', ...new Array(12).fill(`${TABLE_NUM_CELL_WIDTH}px`)];

/**
 * コンポーネントのProps定義
 */
interface Props {
  modalState?: boolean;
  orderData?: Order;
}

const props = withDefaults(defineProps<Props>(), {
  modalState: false,
  orderData: undefined,
});

const emits = defineEmits<{ (e: 'close'): void }>();

const basisData = ref<IOrderBasis | undefined>(undefined);
const chartData = ref<any>(undefined);

/**
 * 以後月一覧
 */
const nextMonthlyDates = computed(() => {
  if (!basisData.value || basisData.value.nextMonthlyInfos.length === 0) return [];
  const startYearMonth = basisData.value.nextMonthlyInfos.reduce((acc, current) =>
    DateService.compareYearMonths(acc.orderBasisTargetYearMonth, current.orderBasisTargetYearMonth) === 1 ? current : acc
  ).orderBasisTargetYearMonth;
  return Array.from({ length: 6 }, (_, i) => DateService.getShiftedIYearMonthByMonths(startYearMonth.year, startYearMonth.month, i));
});

/**
 * 以後月次データ
 */
const nextMonthlyInfo = computed(() => {
  return nextMonthlyDates.value.map((month) =>
    basisData.value?.nextMonthlyInfos.find((pmi) => equalYearMonthInfo(pmi.orderBasisTargetYearMonth, month))
  );
});

/**
 * 以前月一覧
 */
const pastMonthlyDates = computed(() => {
  if (!basisData.value || basisData.value.pastMonthlyInfos.length === 0) return [];
  const endYearMonth = basisData.value.pastMonthlyInfos.reduce((acc, current) =>
    DateService.compareYearMonths(acc.orderBasisTargetYearMonth, current.orderBasisTargetYearMonth) === 1 ? acc : current
  ).orderBasisTargetYearMonth;
  return Array.from({ length: 12 }, (_, i) => DateService.getShiftedIYearMonthByMonths(endYearMonth.year, endYearMonth.month, -11 + i));
});

/**
 * 以前月次データ
 */
const pastMonthlyInfo = computed(() => {
  return pastMonthlyDates.value.map((month) =>
    basisData.value?.pastMonthlyInfos.find((pmi) => equalYearMonthInfo(pmi.orderBasisTargetYearMonth, month))
  );
});

/**
 * エラーモーダルを表示する
 */
const showErrorModal = async () => {
  await showInfoModal({
    modalTitle: t('エラーが発生しました。'),
    modalDescription: t('発注根拠の取得に失敗しました。'),
    isDisabledNextButton: true,
    backButtonLabel: t('OK'),
    colorStyle: 'error',
    icon: 'error',
  });
  closeModal();
};

/**
 * 予入荷を計算して返す
 * @param nextMonthlyData - 以後月次データ
 */
const calcPreArrivalAmount = (nextMonthlyData: IOrderBasisNextMonthlyData | undefined) => {
  if (!nextMonthlyData) return '--';
  return nextMonthlyData.dailyInfos.reduce((acc, dailyInfo) => (acc += dailyInfo.snapshotArrivalAmount), 0).addComma();
};

/**
 * このモーダルを閉じる
 */
const closeModal = () => {
  emits('close');
};

watchEffect(async () => {
  // データがない場合はスキップ
  if (!props.modalState || !props.orderData) {
    basisData.value = undefined;
    chartData.value = undefined;
    return;
  }
  const orderBasisId = props.orderData.orderBasisId;
  if (!orderBasisId) {
    await showErrorModal();
    return;
  }
  // 発注根拠情報取得
  const result = await waitLoading(diContainer.getOrderBasisUseCase.execute(orderBasisId));
  if (!result || result.status.type === 'error') {
    await showErrorModal();
    return;
  }
  basisData.value = result.status.data;

  // チャート描画
  // 登録済みの入荷snapshotをIArrivalに置き換える
  const expandedArrivalInfos = basisData.value.nextMonthlyInfos.flatMap((nextMonthlyInfo) =>
    nextMonthlyInfo.dailyInfos.map((nextDailyInfo) =>
      ArrivalFactory.create({
        amount: nextDailyInfo.snapshotArrivalAmount,
        crossDate: { ...nextMonthlyInfo.orderBasisTargetYearMonth, day: nextDailyInfo.orderBasisTargetDay },
        isFixOrdered: true,
      })
    )
  );
  // 最も納期が後ろの発注済み予入荷を持っておく
  const lastExpandedArrivalInfo = expandedArrivalInfos
    .sort((a, b) => {
      if (!a.crossDate) return -1;
      if (!b.crossDate) return 1;
      return DateService.compareDates(a.crossDate, b.crossDate);
    })
    .at(-1);
  // Chart用のIBusinessを組み立てる
  const graphBusiness = BusinessFactory.create({
    package: basisData.value.snapshotPackage.toString(),
    inventory: basisData.value.snapshotLatestInventoryAmount,
    executionDate: new Date(basisData.value.snapshotExecutionDate),
    MOQ: basisData.value.snapshotMoq,
    customerAnnounceDaily: basisData.value.customerAnnounceDaily ?? [],
    customerAnnounce: basisData.value.nextMonthlyInfos
      .map((info) =>
        AnnounceFactory.create({
          year: info.orderBasisTargetYearMonth.year,
          month: info.orderBasisTargetYearMonth.month,
          amount: info.snapshotCustomerForecastAmount,
        })
      )
      .concat(
        basisData.value.pastMonthlyInfos.map((info) =>
          AnnounceFactory.create({
            year: info.orderBasisTargetYearMonth.year,
            month: info.orderBasisTargetYearMonth.month,
            amount: info.snapshotCustomerForecastAmount,
          })
        )
      ),
    stdInventoryIndices: { 標準在庫指数: basisData.value.userAdjustedStdInventoryIndex },
    recommend: expandedArrivalInfos,
  });
  // Chart用のExtraSimulateを組み立てる
  const graphExtraSimulate = {
    extraAnnounce: basisData.value.nextMonthlyInfos
      .map((info) =>
        AnnounceFactory.create({
          year: info.orderBasisTargetYearMonth.year,
          month: info.orderBasisTargetYearMonth.month,
          amount: info.userAdjustedCustomerForecastAmount,
        })
      )
      .concat(
        basisData.value.pastMonthlyInfos.map((info) =>
          AnnounceFactory.create({
            year: info.orderBasisTargetYearMonth.year,
            month: info.orderBasisTargetYearMonth.month,
            amount: info.snapshotCustomerForecastAmount,
          })
        )
      ),
    extraStartDate: lastExpandedArrivalInfo?.crossDate
      ? new Date(
          DateService.convertIDateToUnixTime(
            DateService.getShiftedIYearMonthByDays(
              lastExpandedArrivalInfo.crossDate.year,
              lastExpandedArrivalInfo.crossDate.month,
              lastExpandedArrivalInfo.crossDate.day,
              1
            )
          )
        )
      : new Date(basisData.value.snapshotExecutionDate),
    extraLeadTime: basisData.value.userAdjustedLeadTime.value,
    extraStdInventory: basisData.value.userAdjustedStdInventoryIndex,
    extraRecommend: expandedArrivalInfos,
  };
  // Chart用にシュミレートを回す
  chartData.value = await changeDayChart(
    graphBusiness,
    null,
    CHART_PERIOD,
    0,
    DateService.convertDateToIDate(new Date(basisData.value.snapshotExecutionDate)),
    graphExtraSimulate
  );
});

/**
 * 単位付きの値をi18nで変換する
 * @param value - 値
 * @param unit - 単位
 * @param needConvert - 値を単位に合わせて変換する必要があるか
 */
const convertNumberWithUnitToStringWithGuard = (value?: number, unit?: string, needConvert = false): string => {
  if (!value || !unit) return '--';
  const convertedValue = needConvert ? convertLeadTimeByUnit(value, unit) : value;
  if (['month', 'months'].includes(unit)) {
    return convertedValue + t('発注根拠モーダル:月');
  } else if (['week', 'weeks'].includes(unit)) {
    return convertedValue + t('発注根拠モーダル:週');
  } else if (['day', 'days'].includes(unit)) {
    return convertedValue + t('発注根拠モーダル:日');
  }
  return convertedValue.toString();
};

/**
 * undefinedガード付きのunixTime変換関数
 * @param date - unixTimeの日付情報(undefined許容)
 */
const convertToDisplayUnixTimeWithGuard = (date?: number) =>
  date ? DateService.convertIYearMonthDayToYYYYMMDDWithSlash(DateService.convertUnixTimeToIDate(date / 1000)) : '--';
</script>
<template>
  <InfoModal
    :modal-state="modalState && !!orderData && !!basisData"
    :back-button-label="t('閉じる')"
    :modal-width="1390"
    is-disabled-next-button
    enable-outside-click
    no-description
    @on-back="closeModal"
  >
    <template #title>
      {{ t('発注根拠詳細') }}
    </template>
    <div class="order-basis-modal__container">
      <div class="row1 base-info__container">
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('発注作成日') }}</div>
          <div class="base-info__item--value">
            {{ convertToDisplayUnixTimeWithGuard(orderData?.createdOrderDate) }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('発注数量') }}</div>
          <div class="base-info__item--value">
            {{ orderData?.initialOrderAmount?.addComma() ?? '--' }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('希望納期') }}</div>
          <div class="base-info__item--value">
            {{ convertToDisplayUnixTimeWithGuard(orderData?.initialDesiredDeliveryDate) }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('標準在庫指数(min)') }}</div>
          <div class="base-info__item--value">
            {{
              convertNumberWithUnitToStringWithGuard(
                basisData?.userAdjustedStdInventoryIndex.min,
                basisData?.userAdjustedStdInventoryIndex.option.unit
              )
            }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('標準在庫指数(max)') }}</div>
          <div class="base-info__item--value">
            {{
              convertNumberWithUnitToStringWithGuard(
                basisData?.userAdjustedStdInventoryIndex.max,
                basisData?.userAdjustedStdInventoryIndex.option.unit
              )
            }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('リードタイム') }}</div>
          <div class="base-info__item--value">
            {{ convertNumberWithUnitToStringWithGuard(basisData?.userAdjustedLeadTime.value, basisData?.userAdjustedLeadTime.unit, true) }}
          </div>
        </div>
      </div>
      <div class="row2">
        <div class="graph__container">
          <Chart
            :date-unit="'day'"
            :width="900"
            :height="400"
            :styles="{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
            }"
            :chart-data="chartData"
          />
        </div>
        <div class="next-info__container">
          <EzTable class="table" :grid-template-columns="NextMonthlyTableGridTemplateColumn" sticky-header sticky-left-columns>
            <template #header>
              <EzHeader class="table--header table--left-header" />
              <template v-for="(header, index) in nextMonthlyDates" :key="index">
                <EzHeader class="table--header">
                  {{ DateService.convertIYearMonthToYYYYMMWithSlash(header, true) }}
                </EzHeader>
              </template>
            </template>
            <template #body>
              <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
                <EzCell class="table--cell table--left-header" :content-padding="8">
                  {{ t('予入荷') }}
                </EzCell>
                <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
                  <EzCell class="table--cell" is-number> {{ calcPreArrivalAmount(targetMonthBasis) }} </EzCell>
                </template>
              </EzTableRow>
              <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
                <EzCell class="table--cell table--left-header" :content-padding="8">
                  {{ t('オリジナル内示') }}
                </EzCell>
                <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
                  <EzCell class="table--cell" is-number>
                    {{ targetMonthBasis?.snapshotCustomerForecastAmount.addComma() ?? '--' }}
                  </EzCell>
                </template>
              </EzTableRow>
              <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
                <EzCell class="table--cell table--left-header" :content-padding="8">
                  {{ t('シミュレーション内示') }}
                </EzCell>
                <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
                  <EzCell class="table--cell" is-number>
                    {{ targetMonthBasis?.userAdjustedCustomerForecastAmount.addComma() ?? '--' }}
                  </EzCell>
                </template>
              </EzTableRow>
              <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
                <EzCell class="table--cell table--left-header" :content-padding="8">
                  {{ t('予末在庫') }}
                </EzCell>
                <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
                  <EzCell class="table--cell" is-number>
                    {{ targetMonthBasis?.snapshotPreInventoryAmount.addComma() ?? '--' }}
                  </EzCell>
                </template>
              </EzTableRow>
              <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
                <EzCell class="table--cell table--left-header" :content-padding="8">
                  {{ t('在庫回転率') }}
                </EzCell>
                <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
                  <EzCell class="table--cell" force-right>
                    {{
                      targetMonthBasis && targetMonthBasis.snapshotCustomerForecastAmount !== 0
                        ? (targetMonthBasis.snapshotPreInventoryAmount / targetMonthBasis.snapshotCustomerForecastAmount).roundToDecimal(2)
                        : '--'
                    }}
                  </EzCell>
                </template>
              </EzTableRow>
            </template>
          </EzTable>
        </div>
      </div>
      <div class="row3 past-info__container">
        <EzTable class="table" :grid-template-columns="PastMonthlyTableGridTemplateColumn" sticky-header>
          <template #header>
            <EzHeader class="table--header" />
            <template v-for="(header, index) in pastMonthlyDates" :key="index">
              <EzHeader class="table--header">{{ DateService.convertIYearMonthToYYYYMMWithSlash(header, true) }}</EzHeader>
            </template>
          </template>
          <template #body>
            <EzTableRow :grid-template-columns="PastMonthlyTableGridTemplateColumn">
              <EzCell class="table--cell" :content-padding="8">
                {{ t('販売実績') }}
              </EzCell>
              <template v-for="(targetMonthBasis, index) in pastMonthlyInfo" :key="index">
                <EzCell class="table--cell" is-number>
                  {{ targetMonthBasis?.snapshotSalesResultAmount.addComma() ?? '--' }}
                </EzCell>
              </template>
            </EzTableRow>
            <EzTableRow :grid-template-columns="PastMonthlyTableGridTemplateColumn">
              <EzCell class="table--cell" :content-padding="8">
                {{ t('内示') }}
              </EzCell>
              <template v-for="(targetMonthBasis, index) in pastMonthlyInfo" :key="index">
                <EzCell class="table--cell" is-number>
                  {{ targetMonthBasis?.snapshotCustomerForecastAmount.addComma() ?? '--' }}
                </EzCell>
              </template>
            </EzTableRow>
            <EzTableRow :grid-template-columns="PastMonthlyTableGridTemplateColumn">
              <EzCell class="table--cell" :content-padding="8">
                {{ t('正答率') }}
              </EzCell>
              <template v-for="(targetMonthBasis, index) in pastMonthlyInfo" :key="index">
                <EzCell class="table--cell" force-right>
                  {{
                    targetMonthBasis
                      ? (
                          (targetMonthBasis.snapshotSalesResultAmount / targetMonthBasis.snapshotCustomerForecastAmount) *
                          100
                        ).roundToDecimal(2) + '%'
                      : '--'
                  }}
                </EzCell>
              </template>
            </EzTableRow>
          </template>
        </EzTable>
      </div>
    </div>
  </InfoModal>
</template>
<style lang="scss" scoped>
.order-basis-modal__container {
  display: grid;
  gap: 24px;
  border-radius: 8px;
  padding: 16px;
  background-color: $semanticPrimarySubdued;
}
.base-info__container {
  display: grid;
  grid-template-columns: repeat(6, auto);
}
.base-info__item {
  display: grid;
  justify-items: center;
  gap: 8px;
  * {
    width: fit-content;
    height: fit-content;
  }
  &--label {
    color: $textSecondary;
  }
  &--value {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    &--error {
      color: $semanticError;
    }
  }
}

.row2 {
  display: grid;
  grid-template-columns: 1fr max-content;
  gap: 16px;
}

.next-info__container {
  max-width: v-bind('`${140 + TABLE_NUM_CELL_WIDTH * 3}px`');
  height: fit-content;
  overflow-x: auto;
}
.table {
  &--header {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    color: $textDefault;
    padding: 11px 0;
    white-space: nowrap;
  }
  &--left-header {
    position: sticky;
    left: 0;
    z-index: 5;
  }
  &--cell {
    font-family: Noto Sans JP;
    font-size: 18px;
    font-weight: 400;
    line-height: 1;
    color: $textSecondary;
    padding: 8px 0;
  }
}
</style>
